import { Field, useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import "./checkbox-list-field.css";
import { useWizardSummaryContext } from "../wizard/wizardSummaryContext";
import CheckboxField from "./checkboxField";
import deleteIcon from '../../images/Delete.svg';
import { SelectableCheckbox } from "../../models/checkbox/SelectableCheckbox";
import { prefixedHexNumberRegex, unprefixedHexNumberRegex } from "../../utilities/regex";
export type CheckboxOption = {
    label: string;
    value: number | string;
}

interface CheckboxListFieldProps {
    options: CheckboxOption[];
    name: string;
    compact?: boolean;
    isSummation?: boolean;
    updateSelectionLabel?: (newLabel) => void;
    allowReinitialise?: boolean;
    scrollable?: boolean
    onDelete?: any
    customDelete?: any
}

function isPowerOfTwo(value: number): boolean {
    return value > 0 && (value & (value - 1)) === 0;
}

/**
 * Normalises a checkbox option mask to a numeric value.
 * Supports numeric input, decimal strings, and hex strings (including 0x-prefixed)
 * so bitmask operations remain consistent across API representations.
 */
const parseMaskValue = (value: number | string): number => {
    if (typeof value === 'number') {
        return value;
    }

    const raw = `${value}`.trim();
    if (!raw) {
        return 0;
    }

    if (prefixedHexNumberRegex.test(raw)) {
        return parseInt(raw, 16);
    }

    const decimalValue = Number(raw);
    const isHexLike = unprefixedHexNumberRegex.test(raw);
    const hexValue = isHexLike ? parseInt(raw, 16) : NaN;

    if (Number.isFinite(decimalValue) && Number.isFinite(hexValue) && decimalValue !== hexValue) {
        // Prefer the representation that produces a valid bit mask.
        if (isPowerOfTwo(hexValue) && !isPowerOfTwo(decimalValue)) {
            return hexValue;
        }
        if (isPowerOfTwo(decimalValue) && !isPowerOfTwo(hexValue)) {
            return decimalValue;
        }
    }

    if (Number.isFinite(decimalValue)) {
        return decimalValue;
    }

    return Number.isFinite(hexValue) ? hexValue : 0;
};

const parseFieldValue = (value: unknown): number => {
    if (typeof value === 'number') {
        return Math.abs(value);
    }
    if (typeof value === 'string') {
        return Math.abs(parseMaskValue(value));
    }
    return 0;
};

const CheckboxListField: FC<CheckboxListFieldProps> = ({ customDelete, onDelete, options, name, compact = false, isSummation = false, updateSelectionLabel = () => { }, allowReinitialise = false, scrollable = true }) => {
    const [listItems, setListItems] = useState<SelectableCheckbox[]>(options.map((option) => { return { ...option, isSelected: false } }));
    const { values } = useFormikContext();
    const { updateSummary } = useWizardSummaryContext();
    const [updateFlag, setUpdateFlag] = useState(true);
    const [selectedItemId, setSelectedItemId] = useState('');

    useEffect(() => {
        if (customDelete)
        {
            setListItems(options.map((option) => { return { ...option, isSelected: false } }))
        }
        setUpdateFlag(false);
    }, [options])

    const isRowHovered = (item) => {
        setSelectedItemId(item.value)
    }

    const handleSelection = (event, form, field, option) => {
        const listItemIndex = listItems.findIndex(item => item.value === option.value);
        const listItem = listItems[listItemIndex];
        const currentListItems = [...listItems];
        currentListItems.splice(listItemIndex, 1);


        if (field.value) {
            // the assumption is that all fields from setting/settingData/element origin uses a summation of selected values
            if (isSummation) {
                const fieldValue = parseFieldValue(field.value);
                const optionMaskValue = parseMaskValue(listItem.value);
                const newFieldValue = listItem.isSelected
                    ? (fieldValue & ~optionMaskValue)
                    : (fieldValue | optionMaskValue);
                form.setFieldValue(name, newFieldValue);
            }
            // the assumption is that all fields from masterDetail origin uses an array for selected values.
            else {
                if (listItem.isSelected) {
                    const newFieldValue = field.value.filter(x => x !== option.value)
                    form.setFieldValue(name, newFieldValue);
                }
                else {
                    form.setFieldValue(name, [
                        ...field.value,
                        option.value
                    ])
                }
            }
        }
        else {
            form.setFieldValue(name, isSummation ? option.value : [option.value])
        }

        const newListItems = [...currentListItems.slice(0, listItemIndex), { ...listItem, isSelected: !listItem.isSelected }, ...currentListItems.slice(listItemIndex)];
        const selectedItems = newListItems.filter(item => item.isSelected);
        updateSelectionLabel(`${selectedItems.length === 1 ? selectedItems[0].label : selectedItems.length} selected`)
        setListItems(newListItems)

        updateSummary(name, newListItems.filter(item => item.isSelected).map(item => item.label).join(', '));
    }

    useEffect(() => {
        // if value is summation typed, sort options, and set is selected if subtract-able.
        const currentFieldValue = name.split('.').reduce((previous, current) => previous?.[current], values as any);

        if (isSummation) {
            const fieldMaskValue = parseFieldValue(currentFieldValue);
            const newListItems = listItems.map(item => {
                const maskValue = parseMaskValue(item.value);
                const isSelected = maskValue > 0 && (fieldMaskValue & maskValue) === maskValue;
                return { ...item, isSelected };
            });
            const selectedItems = newListItems.filter(item => item.isSelected);
            updateSelectionLabel(`${selectedItems.length === 1 ? selectedItems[0].label : selectedItems.length} selected`);
            setListItems(newListItems);

            updateSummary(name, selectedItems.filter(item => item.isSelected).map(item => item.label).join(', '));
        }
        else {
            const newListItems = listItems.map(listItem => {
                if (currentFieldValue && currentFieldValue
                    .includes(listItem.value)) {
                    return { ...listItem, isSelected: true };
                }
                else {
                    return { ...listItem, isSelected: false };
                }
            });
            const selectedItems = newListItems.filter(item => item.isSelected);
            updateSelectionLabel(`${selectedItems.length === 1 ? selectedItems[0].label : selectedItems.length} selected`);
            setListItems(newListItems);
            updateSummary(name, selectedItems.filter(item => item.isSelected).map(item => item.label).join(', '));
        }

        if (allowReinitialise) {
            setUpdateFlag(true);
        }
    }, [updateFlag, values])

    useEffect(() => {
        const nameIndex = name.indexOf('.value');
        const currentFieldValue = name.substring(0, nameIndex > 0 ? nameIndex : name.length).split('.').reduce((previous, current) => {
            return previous?.[current]
        }, values as any);
        if (updateFlag && currentFieldValue && (!isSummation && currentFieldValue.length > 0)) {
            setUpdateFlag(false);
        }
    }, [values])

    return (
        <Field name={name}>
            {({ field, form }) => (
                <div style={{height: "300px"}}  className={`flex flex-col ${compact ? '' : 'border-gray-300 border-solid border-t-2 w-full'} ${scrollable ? 'overflow-y-auto' : ''} h-full divide-y divide-gray-400`}>
                    {listItems.map((option, i) => <>
                        <div onMouseEnter={(e) => isRowHovered(option)}
                            onMouseLeave={(e) => isRowHovered(option)} className="flex flex-row justify-between w-full">
                            <CheckboxField key={i} onSelect={(event, _) => handleSelection(event, form, field, option)} option={option} compact={compact}></CheckboxField>
                            {customDelete ? (option[customDelete.fieldName] === customDelete.comparisonValue && (selectedItemId === option.value && <img
                                id='deleteRecordButton'
                                src={deleteIcon}
                                className="cursor-pointer -ml-6"
                                style={{ width: "18px" }}
                                alt=""
                                onMouseDown={() => onDelete(option.value)}
                            />)) : null
                            }
                        </div></>
                    )}
                </div>
            )}
        </Field>
    );
}

export default CheckboxListField;

